'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';


interface NavigationItem {
  label: string;
  labelDe: string;
  href: string;
}

interface HeaderClientProps {
  className?: string;
}

const HeaderClient = ({ className = '' }: HeaderClientProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState<'en' | 'de'>('en');
  const [isHydrated, setIsHydrated] = useState(false);
  const pathname = usePathname();
  const router = useRouter();

  const navigationItems: NavigationItem[] = [
    { label: 'Home', labelDe: 'Startseite', href: '/hero-home-section' },
    { label: 'Services', labelDe: 'Dienstleistungen', href: '/services-section' },
    { label: 'Projects', labelDe: 'Projekte', href: '/projects-section' },
    { label: 'Blog', labelDe: 'Blog', href: '/blog' },
    { label: 'About', labelDe: 'Über uns', href: '/about-section' },
    { label: 'Contact', labelDe: 'Kontakt', href: '/contact-section' },
  ];

  const translations = {
    en: { getStarted: 'Get Started' },
    de: { getStarted: 'Loslegen' },
  };

  useEffect(() => {
    setIsHydrated(true);
    
    // Detect language from URL pathname
    if (pathname?.startsWith('/de')) {
      setCurrentLanguage('de');
    } else if (pathname?.startsWith('/en')) {
      setCurrentLanguage('en');
    }

    const handleLanguageChange = (event: CustomEvent<{ language: 'en' | 'de' }>) => {
      setCurrentLanguage(event.detail.language);
    };

    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange as EventListener);
    };
  }, [pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    if (!isHydrated || !pathname) return;

    const newLanguage: 'en' | 'de' = currentLanguage === 'en' ? 'de' : 'en';
    
    // Extract path after language prefix
    let pathWithoutLang = pathname;
    if (pathname.startsWith('/en/')) {
      pathWithoutLang = pathname.substring(3); // Remove '/en'
    } else if (pathname.startsWith('/de/')) {
      pathWithoutLang = pathname.substring(3); // Remove '/de'
    } else if (pathname === '/en' || pathname === '/de') {
      pathWithoutLang = '/hero-home-section';
    }

    // Build new URL with new language prefix
    const newPath = `/${newLanguage}${pathWithoutLang}`;

    // Set cookie for preference
    document.cookie = `preferredLanguage=${newLanguage}; path=/; max-age=31536000`;

    // Update state and dispatch event
    setCurrentLanguage(newLanguage);
    const event = new CustomEvent('languageChange', { detail: { language: newLanguage } });
    window.dispatchEvent(event);

    // Navigate to new URL
    router.push(newPath);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'backdrop-blur-md shadow-cosmic' : 'bg-transparent'
        } ${className}`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-28">
            {/* Logo - Fixed size container to prevent overflow */}
            <div className="flex-shrink-0">
              <Link href="/" className="flex items-center group">
                <img
                  src="/assets/images/PPS_logo_long_google_workspace_all-1765574301058.png"
                  alt="Planet Pulse Studios Logo"
                  width={166}
                  height={37}
                  className="w-auto h-12 sm:h-[53px] md:h-[62px]"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8 flex-1 min-w-0 justify-center">
              {navigationItems.map((item) => (
                <Link
                  key={item.href}
                  href={`/${currentLanguage}${item.href}`}
                  className="text-white/90 hover:text-white transition-colors duration-200 whitespace-nowrap"
                >
                  {currentLanguage === 'en' ? item.label : item.labelDe}
                </Link>
              ))}
            </nav>

            {/* Right Section */}
            <div className="flex items-center space-x-4 flex-shrink-0">
              {/* Language Toggle - Interactive */}
              <button
                onClick={toggleLanguage}
                className="hidden md:flex items-center space-x-2 px-3 py-2 rounded-md bg-secondary/50 hover:bg-secondary transition-colors duration-fast text-foreground-light text-sm font-medium"
                aria-label="Toggle language"
              >
                <Icon name="GlobeAltIcon" size={18} variant="outline" />
                <span className="uppercase">{currentLanguage}</span>
              </button>

              {/* CTA Button */}
              <Link
                href={`/${currentLanguage}/contact-section`}
                className="hidden lg:inline-flex items-center justify-center w-40 px-6 py-3 bg-action hover:bg-action/90 text-action-foreground rounded-md font-inter font-semibold text-sm transition-all duration-fast shadow-cta hover:shadow-glow-green animate-pulse-heartbeat whitespace-nowrap"
              >
                {translations[currentLanguage].getStarted}
              </Link>

              {/* Mobile Menu Toggle */}
              <button
                onClick={toggleMobileMenu}
                className="lg:hidden p-2 text-foreground-light hover:text-accent transition-colors duration-fast"
                aria-label="Toggle mobile menu"
              >
                <Icon
                  name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
                  size={24}
                  variant="outline"
                />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-primary border-t border-border">
            <nav className="container mx-auto px-4 py-6 space-y-4">
              {navigationItems.map((item) => (
                <Link
                  key={item.href}
                  href={`/${currentLanguage}${item.href}`}
                  className="block px-4 py-3 text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {currentLanguage === 'en' ? item.label : item.labelDe}
                </Link>
              ))}
              <div className="pt-4 border-t border-border space-y-3">
                <button
                  onClick={toggleLanguage}
                  className="flex items-center space-x-2 w-full px-4 py-3 rounded-md bg-secondary/50 hover:bg-secondary transition-colors duration-fast text-foreground-light text-sm font-medium"
                >
                  <Icon name="GlobeAltIcon" size={18} variant="outline" />
                  <span className="uppercase">{currentLanguage}</span>
                </button>
                <Link
                  href={`/${currentLanguage}/contact-section`}
                  className="block px-4 py-3 bg-action hover:bg-action/90 text-action-foreground rounded-md font-inter font-semibold text-sm transition-all duration-fast shadow-cta hover:shadow-glow-green text-center"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {translations[currentLanguage].getStarted}
                </Link>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default HeaderClient;